import { useCallback, useMemo, useState } from 'react'
import { UserDocument } from '../lib/userDocuments.slice'
import { sortDocuments } from '../lib/helpers'
import { groupByType, GroupByEnum, GroupedDocsEntry } from './helpers'

import { useReselector } from '@/utils/sharedHooks'
import { selectUserDocumentCategoryList } from '@/features/admin/user-document-categories/userDocumentCategories.selectors'
import { RowTypes } from './TableRows'
import { DocumentsTable } from './DocumentsTable'
import { Accordion } from '@/ui'
import { TaxUserDocument } from '@/features/taxes/annual-taxes/taxUserDocuments.slice'

export interface TableOptions {
  sortFn: (_a: UserDocument, _b: UserDocument) => number
  groupBy: GroupByEnum
}

export interface TableHeaderConfig {
  sortBy: string | null
  title: string
}

interface UserDocumentsTableProps {
  columns: TableHeaderConfig[]
  documents: UserDocument[]
  tableOptions: TableOptions
  rowType: RowTypes
  onCheck?: (id: number, checked: boolean) => void
  fixed?: boolean
  taxUserDocuments?: TaxUserDocument[]
}

const getGroupedDocs = (
  groupedDocs: Record<string, UserDocument[]>
): GroupedDocsEntry[] => {
  return Object.entries(groupedDocs).map(([monthKey, docs]) => ({
    monthKey,
    docs,
  }))
}

export const UserDocumentsTable = ({
  columns,
  documents,
  rowType,
  onCheck,
  tableOptions,
  taxUserDocuments,
}: UserDocumentsTableProps) => {
  const useDocSort = (documents: UserDocument[]) => {
    const categories = useReselector(selectUserDocumentCategoryList)
    const [sortVal, setSortVal] = useState<string | null>(null)
    const [sortAsc, setSortAsc] = useState(false)

    const sort = useCallback(
      (value: string | null) => {
        if (value && sortVal === value) {
          setSortAsc((sortAsc) => !sortAsc)
        } else {
          setSortVal(value)
          setSortAsc(false)
        }
      },
      [sortVal]
    )

    const sortedDocs = useMemo(
      () =>
        sortDocuments({
          docs: documents,
          sortAsc,
          sortByVal: sortVal,
          cats: categories,
        }),
      [categories, documents, sortAsc, sortVal]
    )

    return { sort, sortedDocs, sortVal, sortAsc }
  }

  const { sortedDocs, sort, sortAsc } = useDocSort(documents)
  const { groupBy } = tableOptions
  const documentCategories = useReselector(selectUserDocumentCategoryList)

  const grouped = useMemo(
    () =>
      groupByType(groupBy, sortedDocs, taxUserDocuments, documentCategories),
    [groupBy, sortedDocs, taxUserDocuments, documentCategories]
  )

  const documentGroups = Object.entries(grouped)
    .reverse()
    .map(([groupTitle, groupedDocs]) => {
      const hasSubDocuments = groupBy === GroupByEnum.MonthWithinYear

      const subDocuments =
        hasSubDocuments &&
        typeof groupedDocs === 'object' &&
        !Array.isArray(groupedDocs)
          ? getGroupedDocs(groupedDocs)
          : []

      return {
        groupTitle,
        hasSubDocuments,
        subDocuments,
        documents: hasSubDocuments ? [] : (groupedDocs as UserDocument[]),
      }
    })

  const defaultOpenGroups = useMemo(
    () => documentGroups.map(({ groupTitle }) => String(groupTitle)),
    [documentGroups]
  )

  return (
    <div className="mt-10">
      <Accordion
        variant="header"
        type="multiple"
        defaultValue={defaultOpenGroups}
      >
        {documentGroups.map(
          ({ groupTitle, hasSubDocuments, subDocuments, documents }) => (
            <Accordion.Item
              value={String(groupTitle)}
              className="my-2"
              key={`accordion-item-${groupTitle}`}
            >
              <Accordion.Trigger className="text-moss-700 bg-green-100/10 my-1 px-2 pl-4 rounded-lg data-[state=open]:text-green-700">
                {String(groupTitle)}
              </Accordion.Trigger>

              <Accordion.Content className="w-full">
                <DocumentsTable
                  columns={columns}
                  documents={documents}
                  rowType={rowType}
                  onCheck={onCheck}
                  sort={sort}
                  sortAsc={sortAsc}
                  subDocuments={subDocuments}
                  hasSubDocuments={hasSubDocuments}
                />
              </Accordion.Content>
            </Accordion.Item>
          )
        )}
      </Accordion>
    </div>
  )
}
